import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Paper,
  TableRow,
  TextField,
} from "@mui/material";

const ReusableWageTable = ({ data, handleInputChange, category, disabled, errors = {} }) => {
  const categories = ["Male", "Female", "Total"];
  const financialYears = ["currentFinancialYear", "previousFinancialYear"];

  const getFieldValue = (year, categoryType, field, subField) => {
    const v = data?.[year]?.[category]?.[categoryType]?.[field]?.[subField];
    return v ?? "";
  };

  const getErrorKey = (year, categoryType, field) => {
    const mainField = field.split('.')[0]; // Get 'equalWageMin' or 'equalWageMax' or 'total'
    return `${year}_${category}_${categoryType}_${mainField}`;
  };

  const hasError = (year, categoryType, field) => {
    const errorKey = getErrorKey(year, categoryType, field);
    return !!errors[errorKey];
  };

  const renderTextField = (
    value,
    onChange,
    isTotalRow,
    placeholder,
    isDisabled = false,
    hasError = false
  ) => (
    <>
    <TextField
      // value={value === 0 ? "0" : value || ""}
      // value={value === null || value === undefined ? "" : value.toString()}
      value = {value ?? ""}
      onChange={onChange}
      variant="standard"
      error={hasError}
      InputProps={{
        disableUnderline: true,
        readOnly: isTotalRow,
      }}
      sx={{ 
        "& .MuiInputBase-input": { 
          padding: 0,
          lineHeight: "1.4rem", 
          height:"1.4rem", 
          textAlign:"center", 
          whiteSpace:"nowrap", 
          overflow:"hidden", 
          textOverflow:"ellipsis",
          // borderBottom: hasError ? "2px solid #d32f2f" : "none"
        },
        "& .MuiInputBase-input::placeholder": {
          fontSize:"14px",
          textAlign:"center",
          color: "gray",
        },
      }}
      placeholder={isTotalRow ? "Total" : placeholder}
      disabled={(placeholder==="%" && true)||isDisabled || isTotalRow ||  disabled
    }
    />
    </>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ mb: 3, borderRadius: "10px", width: "99%", marginLeft: "0.9rem" }}
    >
      <Table
        sx={{
          "& .MuiTableCell-root": { border: "1px solid #ccc" },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#1C486B",
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            padding: "4px 8px",
            height: 40,
          },
          tableLayout: "fixed",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "10%" }} rowSpan={3}>
              Category
            </TableCell>
            <TableCell sx={{ width: "30%" }} colSpan={5}>
              Current Financial Year
            </TableCell>
            <TableCell sx={{ width: "30%" }} colSpan={5}>
              Previous Financial Year
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Total</TableCell>
            <TableCell colSpan={2}>Equal to Minimum Wage</TableCell>
            <TableCell colSpan={2}>More than Minimum Wage</TableCell>
            <TableCell>Total</TableCell>
            <TableCell colSpan={2}>Equal to Minimum Wage</TableCell>
            <TableCell colSpan={2}>More than Minimum Wage</TableCell>
          </TableRow>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Number</TableCell>
            <TableCell>%</TableCell>
            <TableCell>Number</TableCell>
            <TableCell>%</TableCell>
            <TableCell></TableCell>
            <TableCell>Number</TableCell>
            <TableCell>%</TableCell>
            <TableCell>Number</TableCell>
            <TableCell>%</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {categories.map((categoryType, index) => {
            const isTotalRow = index === 2;
            const key =
              categoryType === "Total"
                ? "totalAddition"
                : categoryType.toLowerCase();

            return (
              <TableRow key={categoryType}>
                <TableCell style={{ fontWeight: "bold" }}>
                  {categoryType}
                </TableCell>

                {financialYears.map((year) => (
                  <React.Fragment key={year}>
                    {/* Total */}
                    <TableCell
                      style={{
                        backgroundColor: isTotalRow ? "#DBDBDB" : "transparent",
                      }}
                    >
                        {renderTextField(
                          isTotalRow
                           ? (data?.[year]?.[category]?.[key]?.total ?? "")
                           : (data?.[year]?.[category]?.[key]?.total ?? ""),
                          (e) =>
                            handleInputChange(e, category, year, "total", key),
                          isTotalRow,
                         "Enter No.",
                         false,
                         hasError(year, key, "total")
                        )}
                    </TableCell>

                    {/* equalWageMin.number */}
                    <TableCell
                      style={{
                        backgroundColor: isTotalRow ? "#DBDBDB" : "transparent",
                      }}
                    >
                      {renderTextField(
                        getFieldValue(year, key, "equalWageMin", "number"),
                        (e) =>
                          handleInputChange(
                            e,
                            category,
                            year,
                            "equalWageMin.number",
                            key
                          ),
                        isTotalRow,
                        "Enter No.",
                        false,
                        hasError(year, key, "equalWageMin.number")
                      )}
                    </TableCell>

                    {/* equalWageMin.percentage */}
                    <TableCell style={{ backgroundColor: "#DBDBDB" }}>
                      {renderTextField(
                        getFieldValue(year, key, "equalWageMin", "percentage"),
                        (e) =>
                          handleInputChange(
                            e,
                            category,
                            year,
                            "equalWageMin.percentage",
                            key
                          ),
                        isTotalRow,
                        "%",
                        isTotalRow,
                        false
                      )}
                    </TableCell>

                    {/* equalWageMax.number */}
                    <TableCell
                      style={{
                        backgroundColor: isTotalRow ? "#DBDBDB" : "transparent",
                      }}
                    >
                      {renderTextField(
                        getFieldValue(year, key, "equalWageMax", "number"),
                        (e) =>
                          handleInputChange(
                            e,
                            category,
                            year,
                            "equalWageMax.number",
                            key
                          ),
                        isTotalRow,
                        "Enter No.",
                        false,
                        hasError(year, key, "equalWageMax.number")
                      )}
                    </TableCell>

                    {/* equalWageMax.percentage */}
                    <TableCell style={{ backgroundColor: "#DBDBDB" }}>
                      {renderTextField(
                        getFieldValue(year, key, "equalWageMax", "percentage"),
                        (e) =>
                          handleInputChange(
                            e,
                            category,
                            year,
                            "equalWageMax.percentage",
                            key
                          ),
                        isTotalRow,
                        "%",
                        isTotalRow,
                        false
                      )}
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReusableWageTable;
